import React from 'react';
import { useSubscription } from '../context/SubscriptionContext';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ChevronLeft } from 'lucide-react';

interface PremiumGuardProps {
  children: React.ReactNode;
  requiredPlan?: 'pro' | 'premium';
}

export const PremiumGuard: React.FC<PremiumGuardProps> = ({ children, requiredPlan = 'premium' }) => {
  const { effectivePlan } = useSubscription();
  const navigate = useNavigate();

  const hasAccess = (() => {
    if (effectivePlan === 'premium') return true;
    if (requiredPlan === 'pro' && effectivePlan === 'pro') return true;
    return false;
  })();

  if (hasAccess) {
    return <>{children}</>;
  }

  return (
    <>
      {/* Blurred background — stays in normal flow */}
      <div className="flex-1 w-full flex flex-col filter blur-sm select-none pointer-events-none opacity-30 overflow-hidden">
        {children}
      </div>

      {/* Fixed overlay — covers full viewport including Sidebar and TopBar */}
      <div className="fixed inset-0 flex items-center justify-center bg-black/70 backdrop-blur-[3px] z-[999] p-4 text-center select-none">
        <button
          onClick={() => navigate('/dashboard')}
          className="absolute top-6 left-6 flex items-center gap-1.5 text-neutral-400 hover:text-white transition-colors text-xs font-bold"
        >
          <ChevronLeft size={16} />
          Back
        </button>
        <div className="max-w-md w-full bg-[#0e0f17] border border-neutral-900 rounded-3xl p-6 md:p-8 shadow-2xl space-y-5 animate-in fade-in zoom-in-95 duration-500">
          <div className="w-12 h-12 md:w-16 md:h-16 bg-red-500/10 border border-red-500/30 rounded-2xl flex items-center justify-center mx-auto text-red-500 shadow-inner">
            <Sparkles size={24} className="animate-pulse" />
          </div>

          <div className="space-y-2">
            <h2 className="text-xl md:text-2xl font-black tracking-tight text-white">Subscribe to Unlock</h2>
            <p className="text-xs text-neutral-400 leading-relaxed">
              This feature is exclusive to {requiredPlan === 'premium' ? 'Premium' : 'Pro / Premium'} members. Upgrade your plan to get instant access and elevate your trading.
            </p>
          </div>

          <button
            onClick={() => navigate('/pricing')}
            className="w-full py-3 md:py-3.5 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl transition-all text-sm shadow-lg shadow-red-950/20 active:scale-[0.98]"
          >
            Upgrade Plan Now
          </button>
        </div>
      </div>
    </>
  );
};

export default PremiumGuard;
